import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, RefreshCcw } from "lucide-react";
import { api } from "../lib/api";

// Feste Slot-Positionen rund um den Hero-Titel (links/rechts vom Text)
const SLOTS = [
  { top: "8%",  left: "8%",  size: 110, delay: "0s"   },
  { top: "44%", left: "3%",  size: 130, delay: "1.0s" },
  { top: "72%", left: "14%", size: 100, delay: "2.0s" },
  { top: "20%", left: "22%", size: 90,  delay: "0.5s" },
  { top: "62%", left: "28%", size: 80,  delay: "2.6s" },
  { top: "10%", left: "72%", size: 110, delay: "0.3s" },
  { top: "38%", left: "82%", size: 130, delay: "1.7s" },
  { top: "70%", left: "76%", size: 100, delay: "2.4s" },
  { top: "20%", left: "88%", size: 80,  delay: "0.9s" },
  { top: "58%", left: "92%", size: 90,  delay: "3.1s" },
];

const categoryGlow = (cat) => {
  switch (cat) {
    case "ambassador": return "drop-shadow(0 0 20px rgba(255,184,77,0.55))";
    case "staff":      return "drop-shadow(0 0 20px rgba(124,77,255,0.55))";
    case "events":     return "drop-shadow(0 0 20px rgba(80,200,255,0.50))";
    case "games":      return "drop-shadow(0 0 20px rgba(120,255,150,0.50))";
    case "fansite":    return "drop-shadow(0 0 20px rgba(255,120,180,0.50))";
    default:           return "drop-shadow(0 0 18px rgba(255,255,255,0.25))";
  }
};

const Hero = ({ minimal = false }) => {
  const [habbos, setHabbos] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [settings, setSettings] = useState(null);

  const loadHabbos = async () => {
    setRefreshing(true);
    try {
      // Iter51c: nur so viele Habbos anfordern wie Slots existieren – sonst Overlap.
      const data = await api.heroFloaters(SLOTS.length);
      setHabbos(data.slice(0, SLOTS.length));
    } catch (e) {
      console.error("Hero floaters load failed", e);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadHabbos();
    (async () => { try { setSettings(await api.getSettings()); } catch { /* ignore */ } })();
  }, []);

  const title = settings?.hero_title || "HABTHEK";
  const subtitle = settings?.hero_subtitle || "Die Geschichte hinter den Pixeln";
  const subtitleFont = settings?.hero_subtitle_font || "handwriting";
  const badgeText = settings?.hero_badge_text || "Inoffizielle Fanseite · Habbo Historie";
  const SUBTITLE_FONT_CLASS = {
    handwriting: "handwriting text-2xl md:text-3xl",
    pixel:       "pixel-text text-xl md:text-3xl tracking-wider",
    serif:       "font-serif italic text-2xl md:text-3xl",
    mono:        "font-mono text-xl md:text-2xl",
    sans:        "font-sans text-2xl md:text-3xl",
  };
  const subtitleClass = SUBTITLE_FONT_CLASS[subtitleFont] || SUBTITLE_FONT_CLASS.handwriting;

  return (
    <section className="relative overflow-hidden">
      <div className="relative mx-auto max-w-7xl px-4 pt-12 pb-20 md:pt-20 md:pb-28">
        {/* Schwebende Habbos + Badges + Moebel (Iter50) */}
        <div className="absolute inset-0 pointer-events-none">
          {habbos.map((h, i) => {
            const slot = SLOTS[i % SLOTS.length];
            // Iter50: API liefert Habbo/Badge/Furni gemischt; legacy `avatar` -> `image_url`
            const itemType = h.type || "habbo";
            const imgSrc = h.image_url || h.avatar;
            const label = h.label || h.name;
            const role = h.role || (itemType === "badge" ? "Badge" : itemType === "furni" ? "Moebel" : "");
            // Glow je Typ
            const glow = itemType === "badge"
              ? "drop-shadow(0 0 14px rgba(255,184,77,0.55))"
              : itemType === "furni"
              ? "drop-shadow(0 0 14px rgba(124,77,255,0.5))"
              : categoryGlow(h.category);
            // Badges + Moebel sind kleiner als Habbos
            const sizeStyle = itemType === "habbo"
              ? { width: slot.size, filter: glow }
              : { width: Math.round(parseInt(slot.size, 10) * 0.55), filter: glow };
            return (
              <div
                key={`${itemType}-${label}-${i}`}
                className="floating-chair absolute group pointer-events-auto"
                style={{ top: slot.top, left: slot.left, animationDelay: slot.delay }}
                title={`${label}${role ? " – " + role : ""}`}
                data-testid={`hero-floater-${itemType}-${label}`}
              >
                <img
                  src={imgSrc}
                  alt={label}
                  className="hb-pixel-img select-none"
                  style={sizeStyle}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.opacity = "0.25"; }}
                />
                <div className="pointer-events-none absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap rounded-md border border-white/10 bg-[#0f141b]/95 px-2 py-1 text-[11px] text-zinc-200 opacity-0 transition-opacity group-hover:opacity-100">
                  <span className="font-semibold text-white">{label}</span>
                  {role && <span className="ml-1 text-[#9d7bff]">· {role}</span>}
                </div>
              </div>
            );
          })}
        </div>

        <div className={`relative z-10 mx-auto max-w-3xl text-center ${minimal ? "pointer-events-none opacity-0" : ""}`}>
          <div className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-wider text-zinc-300" data-testid="hero-badge">
            <Sparkles className="h-3.5 w-3.5 text-[#ffb84d]" />
            {badgeText}
          </div>

          <h1 data-testid="hero-title" className="pixel-text text-4xl sm:text-6xl md:text-7xl leading-[1.05] text-white">
            {title}<span className="text-[#7c4dff]">.</span>
          </h1>
          <p data-testid="hero-subtitle" className={`${subtitleClass} mt-4 text-zinc-300`}>
            {subtitle}
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/historie" className="btn-primary inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold">
              Zur Zeitleiste <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/news" className="btn-ghost inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold">
              Aktuelle Artikel
            </Link>
            <button
              onClick={loadHabbos}
              disabled={refreshing}
              className="btn-ghost inline-flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold"
              title="Neue zufällige Habbos laden"
            >
              <RefreshCcw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Habbos mischen
            </button>
          </div>

          <div className="mt-6 text-[11px] text-zinc-500">
            Botschafter:innen, Staff & Community-Habbos – live von <span className="text-zinc-300">habbo.de</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
